import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';

const HealthGoalsTestsModal = ({ isOpen, onClose, onSave, test, goals, mode }) => {
  const [formData, setFormData] = useState({
    goal_id: '',
    test_name: '',
    test_code: '',
    is_optional: false
  });

  const [allTests, setAllTests] = useState([]);
  const [testSearch, setTestSearch] = useState('');
  const [showTestDropdown, setShowTestDropdown] = useState(false);

  // Fetch all tests from admin/test endpoint
  useEffect(() => {
    const fetchTests = async () => {
      try {
        const response = await fetch('https://vitaldev.vitalinsights.in/admin/test', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        setAllTests(data.data || []);
      } catch (error) {
        console.error('Error fetching tests:', error);
      }
    };
    fetchTests();
  }, []);

  useEffect(() => {
    if (mode === 'edit' && test) {
      setFormData({
        goal_id: test.goal_id || '',
        test_name: test.test_name || '',
        test_code: test.test_code || '',
        is_optional: test.is_optional || false
      });
    } else {
      setFormData({
        goal_id: '',
        test_name: '',
        test_code: '',
        is_optional: false
      });
    }
    setTestSearch('');
  }, [mode, test, isOpen]);

  const filteredTests = allTests.filter(t =>
    t.name.toLowerCase().includes(testSearch.toLowerCase()) ||
    t.test_code.toLowerCase().includes(testSearch.toLowerCase())
  );

  const handleSelectTest = (selectedTest) => {
    setFormData(prev => ({
      ...prev,
      test_name: selectedTest.name,
      test_code: selectedTest.test_code
    }));
    setTestSearch('');
    setShowTestDropdown(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.goal_id) {
      alert('Please select a goal');
      return;
    }
    if (!formData.test_name.trim()) {
      alert('Please enter test name');
      return;
    }
    if (!formData.test_code.trim()) {
      alert('Please enter test code');
      return;
    }

    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-800">
            {mode === 'create' ? 'Add New Health Goal Test' : 'Edit Health Goal Test'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Goal Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Goal *
            </label>
            <select
              name="goal_id"
              value={formData.goal_id}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select a goal</option>
              {goals.map((goal) => (
                <option key={goal.id} value={goal.id}>
                  {goal.goal_name}
                </option>
              ))}
            </select>
          </div>

          {/* Test Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Test *
            </label>
            <div className="relative mb-3">
              <input
                type="text"
                value={testSearch}
                onChange={(e) => setTestSearch(e.target.value)}
                onFocus={() => setShowTestDropdown(true)}
                onBlur={() => setTimeout(() => setShowTestDropdown(false), 200)}
                placeholder="Search by test name or code..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {showTestDropdown && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto z-10">
                  {filteredTests.length > 0 ? (
                    filteredTests.map(t => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => handleSelectTest(t)}
                        className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors border-b last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{t.name}</div>
                        <div className="text-xs text-gray-500">{t.test_code}</div>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-gray-500">No tests found</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Test Name Display */}
          {formData.test_name && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Test Name</label>
                  <p className="text-sm font-medium text-gray-900">{formData.test_name}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Test Code</label>
                  <p className="text-sm font-medium text-gray-900">{formData.test_code}</p>
                </div>
              </div>
            </div>
          )}

          {/* Optional Checkbox */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="is_optional"
                checked={formData.is_optional}
                onChange={handleInputChange}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Optional Test</span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              {mode === 'create' ? 'Create Test' : 'Update Test'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HealthGoalsTestsModal;
